<script>
  import { fade } from "svelte/transition";
  import { logButtonClick, logTiming } from "@services/analytics";
  import { _ } from "@services/i18n";
  import {
    suggestedQuestionsTimerCancelled
  } from "@stores/features/suggested-questions";
  import { activeVideo, experience } from "@stores/global";
  import { toggleActiveVideo } from "@components/Video";
  import {
    startRecording,
    stopRecording
  } from "@lib/client/audio";
  import { getIntent } from "@services/speakeasy";
  import { FADE_DURATION, VIDEOS } from "../consts";

  let recording = false;
  let waiting = false;

  let text = $_("components.talk_button.text.default", { default: "Talk" });

  $: if (recording && !waiting) {
    text = $_("components.talk_button.text.recording", { default: "Listening" });
  } else if (recording && waiting) {
    text = $_("components.talk_button.text.waiting", { default: "Pending" });
  } else {
    text = $_("components.talk_button.text.default", { default: "Talk" });
  }

  /**
   * Start recording users microphone
   */
  function start() {
    // Show the UI now, update on response from getUserMedia
    recording = true;
    suggestedQuestionsTimerCancelled.set(true);

    if ($activeVideo === VIDEOS.CONVERSATION) {
      toggleActiveVideo().andPlay();
    }

    startRecording();
  }

  /**
   * Stop recording users microphone, send audio clip
   * to nlu, and play the video
   */
  async function stop() {
    logButtonClick("talk_button");

    recording = false;
    waiting = true;
    const touchEnded = Date.now();

    const onError = () => {
      logTiming(
        "talk_button",
        "video_start",
        Date.now() - touchEnded,
        "Time from talk button touchend to first frame"
      );
      waiting = false;
    };

    try {
      const audio = await stopRecording();
      await getIntent({
        type: "audio",
        payload: audio,
        experienceId: $experience.id,
        onAfterSubmit: () => { waiting = false; },
        onError,
        onVideoPlayback: () => {
          logTiming(
            "talk_button",
            "video_start",
            Date.now() - touchEnded,
            "Time from talk button touchend to first frame"
          );
        }
      });
    } catch (e) {
      onError();
    }
  }
</script>

<style>
  .talk {
    background: var(--thin-shadow);
    border: 0.44rem solid var(--primary-color);
    border-radius: 50%;
    bottom: 2rem;
    box-sizing: border-box;
    color: transparent;
    cursor: pointer;
    display: block;
    font-size: 2px;
    height: 9.6rem;
    left: 50%;
    opacity: 1;
    overflow: hidden;
    padding: 0;
    pointer-events: auto;
    position: absolute;
    transform: translateX(-50%);
    transition: opacity 150ms ease-out;
    width: 9.6rem;
    z-index: 3;
  }

  .talk::after {
    animation: spin 2s infinite linear;
    background: url("/images/gradient.png") no-repeat center center;
    border-radius: 50%;
    bottom: 0.3rem;
    box-sizing: border-box;
    content: "";
    left: 0.3rem;
    opacity: 0;
    pointer-events: none;
    position: absolute;
    right: 0.3rem;
    top: 0.3rem;
    transition: opacity 1s ease-in-out;
  }

  @supports (
    background: conic-gradient(var(--primary-color), var(--primary-color), var(--thin-shadow))
  ) {
    .talk::after {
      background: conic-gradient(
        var(--primary-color),
        var(--primary-color),
        var(--thin-shadow)
      );
    }
  }

  @media (max-width: 320px) {
    .talk {
      bottom: 1rem;
      transform: translateX(-50%) scale(0.8);
    }
  }

  .recording ~ .ring:nth-of-type(1)::before {
    animation: loop 2s linear 0s infinite;
  }

  .recording ~ .ring:nth-of-type(1)::after {
    animation: loop 2s linear 0.66s infinite;
  }

  .recording ~ .ring:nth-of-type(2)::before {
    animation: loop 2s linear 1.32s infinite;
  }

  .waiting ~ .ring {
    opacity: 0;
    transition: opacity 0.25s ease-out;
  }

  .waiting::after {
    opacity: 1;
    transition: opacity 0.25s ease-out;
  }

  .talk::before {
    background: var(--primary-color);
    border-radius: 50%;
    bottom: 0.3rem;
    box-sizing: border-box;
    content: "";
    left: 0.3rem;
    pointer-events: none;
    position: absolute;
    right: 0.3rem;
    top: 0.3rem;
  }

  .ring {
    bottom: 2rem;
    height: 9.6rem;
    left: 50%;
    pointer-events: none;
    position: absolute;
    transform: translateX(-50%);
    width: 9.6rem;
    z-index: 3;
  }

  .ring::before,
  .ring::after {
    border: 0.2rem solid var(--primary-color);
    border-radius: 50%;
    box-sizing: border-box;
    content: "";
    height: 9.6rem;
    opacity: 0;
    position: absolute;
    width: 9.6rem;
  }

  @media (max-width: 320px) {
    .ring {
      bottom: 1rem;
    }
  }

  @keyframes loop {
    0% {
      opacity: 0;
      transform: scale(1);
    }

    20% {
      opacity: 0.5;
    }

    100% {
      opacity: 0;
      transform: scale(2);
    }
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }

    to {
      transform: rotate(360deg);
    }
  }
</style>

<div
  class="talk"
  class:recording
  class:waiting
  on:contextmenu|preventDefault|stopPropagation
  on:mousedown={start}
  on:mouseup={stop}
  on:touchstart={start}
  on:touchcancel={stop}
  on:touchend={stop}
  transition:fade={{ duration: FADE_DURATION }}>
  {text}
</div>
<i class="ring" />
<i class="ring" />
